import { db, functions, auth } from '@/shared/services/firebase'
import axios from '@hw-core/libs/axios'

export default {
  namespaced: true,
  state: {},
  getters: {},
  mutations: {},
  actions: {
    fetchShifts(ctx, { accountId, onlyAreas, callback }) {
      let query = db.collection('shifts')
        .where('start', '>=', new Date())
        .where('status', 'in', ['booked'])
        .where('reserved', '==', false)
      if(accountId) {
        query = query.where('accountId', '==', accountId)
      }
      query
        .limit(900)
        .onSnapshot(async snapshot => {
          const changes = snapshot.docChanges()
          await Promise.all(changes.map(async change => {
            let data = { ...change.doc.data(), id: change.doc.id }
            if (onlyAreas && onlyAreas.indexOf(data.areaId) === -1) {
              return
            }

            const [resUser, resArea, resAccount] = await Promise.allSettled([
              db.collection('users')
                .doc(data.assignedTo)
                .get()
                .then(doc => {
                  if (doc.exists) {
                    return doc.data()
                  }
                    return {
                      firstName: '',
                      lastName: '',
                      email: '',
                      mobile: '',
                      role: '',
                      profession: '',
                    }
                }),
              db.collection('areas')
                .doc(data.areaId)
                .get()
                .then(doc => {
                  if (doc.exists) {
                    return doc.data()
                  }
                  return { name: '' }
                }),
                db.collection('accounts')
                .doc(data.accountId)
                .get()
                .then(doc => {
                  if (doc.exists) {
                    return doc.data()
                  }
                  return null
                }),
            ])

            if (resUser.status === 'fulfilled') {
              data = { ...data, user: resUser.value }
            }
            if (resArea.status === 'fulfilled') {
              data = { ...data, area: resArea.value }
            }
            if (resAccount.status === 'fulfilled') {
              data = { ...data, account: resAccount.value }
            }
            callback({ type: change.type, data })
          }))
        })
    },

    updateShift(ctx, { id, updates }) {
      return new Promise((resolve, reject) => {
        db.collection('shifts').doc(id).update({ ...updates }).then(() => {
          resolve()
        })
          .catch(err => {
            reject(err)
          })
      })
    },

    fetchUserDetails(ctx, { assignedTo }) {
      return new Promise((resolve, reject) => {
        db.collection('users').doc(assignedTo)
          .get()
          .then(doc => {
            if (!doc.exists) {
              reject()
            } else {
              resolve(doc.data())
            }
          })
          .catch(() => {
            reject()
          })
      })
    },

    sendVerificationEmail(ctx, { to, message, subject }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${functions.url}/sendEmail`, { to, message, subject })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },

    async sendEmailWithCC(ctx, { to, cc, subject, message }) {
      await axios.post(`${functions.url}/sendEmailWithCC`, { to, cc, subject, message })
    },

    createZendeskTicket(ctx, {
      requester, groupId, message, subject, collaborators,
    }) {
      return new Promise((resolve, reject) => {
        auth.currentUser.getIdToken(true).then(idToken => {
          axios
            .post(`${functions.url}/createZendeskTicket`, {
              requester, groupId, message, subject, collaborators, token: idToken,
            })
            .then(response => resolve(response))
            .catch(error => reject(error))
        })
          .catch(error => {
            reject(error)
          })
      })
    },

    updateZendeskTicket(ctx, {
      requester, ticketId, message, collaborators,
    }) {
      return new Promise((resolve, reject) => {
        auth.currentUser.getIdToken(true).then(idToken => {
          axios
            .post(`${functions.url}/updateZendeskTicket`, {
              requester, ticketId, message, collaborators, token: idToken,
            })
            .then(response => resolve(response))
            .catch(error => reject(error))
        })
          .catch(error => {
            reject(error)
          })
      })
    },

    fetchShiftPikapalkka(ctx, {
      accountId, onlyAreas, isAllAccount = false, callback,
    }) {
      let query = db.collection('shifts')
      if (!isAllAccount) {
        query = query.where('accountId', '==', accountId)
      }
      query.where('end', '<=', new Date())
        .where('status', '==', 'confirmed')
        .where('boostedOptions.boostType', '==', 'quick')
        .where('finalConfirmation.confirmed', '==', false)
        .where('confirmationRequest', '==', '')
        .onSnapshot(snapshot => {
          const changes = snapshot.docChanges()
          changes.forEach(change => {
            let data = { ...change.doc.data(), id: change.doc.id }
            if (onlyAreas && onlyAreas.indexOf(data.areaId) === -1) {
              return
            }
            if(!data.assignedTo) {
              return
            }
            Promise.allSettled([
              db.collection('users')
                .doc(data.assignedTo)
                .get()
                .then(doc => {
                  if (doc.exists) {
                    return { ...doc.data(), role: ctx.rootGetters.userRole(doc.data().role) }
                  }
                  return null
                }),
              db.collection('areas')
                .doc(data.areaId)
                .get()
                .then(doc => {
                  if (doc.exists) {
                    return doc.data()
                  }
                  return {
                    name: '',
                  }
                }),
            ]).then(([...res]) => {
              const [resUser, resArea] = res
              if (resUser.status === 'fulfilled') {
                data = { ...data, user: resUser.value }
              }
              if (resArea.status === 'fulfilled') {
                data = { ...data, area: resArea.value }
              }

              callback({ type: change.type, data })
            }).catch(() => {
            })
          })
        })
    },

    fetchShiftPast(ctx, { accountId, onlyAreas }) {
      return new Promise((resolve, reject) => {
        // Set max age to 1 month
        const maxAge = new Date()
        maxAge.setMonth(maxAge.getMonth() - 1)
        db.collection('shifts')
          .where('accountId', '==', accountId)
          .where('end', '<=', new Date())
          .where('end', '>=', maxAge)
          .where('status', 'in', ['verified', 'confirmed', 'booked'])
          .where('finalConfirmation.confirmed', '==', false)
          .where('confirmationRequest', '==', '')
          .get()
          .then(async snapshots => {
            try {
              const shifts = await Promise.all(snapshots.docs.map(async shiftDoc => {
                let data = { ...shiftDoc.data(), id: shiftDoc.id }
                if (data.boostedOptions && data.boostedOptions.boostType === 'quick') {
                  return null
                }
                if (onlyAreas && onlyAreas.indexOf(data.areaId) === -1) {
                  return null
                }
                const [resUser, resArea] = await Promise.allSettled([
                  db.collection('users')
                    .doc(data.assignedTo)
                    .get()
                    .then(doc => {
                      if (doc.exists) {
                        return doc.data()
                      }
                      return null
                    }),
                  db.collection('areas')
                    .doc(data.areaId)
                    .get()
                    .then(doc => {
                      if (doc.exists) {
                        return doc.data()
                      }
                      return { name: '' }
                    }),
                ])
                if (resUser.status === 'fulfilled') {
                  data = { ...data, user: resUser.value }
                }
                if (resArea.status === 'fulfilled') {
                  data = { ...data, area: resArea.value }
                }
                return data
              }))
              resolve(shifts.filter(shift => shift !== null))
            } catch (e) {
              reject(e)
            }
          })
          .catch(err => {
            reject(err)
          })
      })
    },

    sendSMS(ctx, { mobile, message }) {
      return new Promise((resolve, reject) => {
        auth.currentUser.getIdToken(true).then(idToken => {
          axios
            .post(`${functions.url}/sendSMSMessage`, { to: mobile, message, token: idToken })
            .then(response => resolve(response))
            .catch(error => reject(error))
        }).catch(error => {
          reject(error)
        })
      })
    },

    createClientRequest(ctx, clientRequest) {
      return new Promise((resolve, reject) => {
        db.collection('clientRequests').add({
          ...clientRequest,
          createdAt: new Date(),
        })
          .then(response => {
            resolve(response.id)
          })
          .catch(err => {
            reject(err)
          })
      })
    },
  },
}
